import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

/** Duración de la animación de salida (debe coincidir con `.anim-modal-out` en index.css). */
const DURACION_SALIDA_MS = 180;

// ── Bloqueo de scroll del documento (admite popups anidados) ─────────────────

let bloqueosScroll = 0;
let overflowPrevio = '';

function bloquearScroll() {
  if (bloqueosScroll === 0) {
    overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  bloqueosScroll += 1;
}

function liberarScroll() {
  bloqueosScroll = Math.max(0, bloqueosScroll - 1);
  if (bloqueosScroll === 0) document.body.style.overflow = overflowPrevio;
}

// ── Pila de popups: Escape cierra solo el que está encima ────────────────────

const pilaModales: number[] = [];
let siguienteModalId = 1;

/** Cierra el popup actual con animación (para botones "Cancelar"/"Cerrar" dentro del contenido). */
const ModalCloseContext = createContext<() => void>(() => undefined);

export function useCerrarModal(): () => void {
  return useContext(ModalCloseContext);
}

/** Botón dentro de un <Modal> que lo cierra con la animación de salida. */
export function ModalCloseButton({ children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  const cerrar = useCerrarModal();
  return (
    <button type="button" {...props} onClick={cerrar}>
      {children}
    </button>
  );
}

type ModalProps = {
  children: ReactNode;
  onClose: () => void;
  /** Clase de ancho máximo del panel (por defecto `max-w-3xl`). */
  maxWidthClassName?: string;
  /** Muestra el botón de cerrar (X) en la esquina superior derecha. */
  showCloseButton?: boolean;
  /** Permite cerrar con clic en el fondo y con Escape. */
  dismissible?: boolean;
  /** Clases extra del panel (padding, altura, etc.). */
  panelClassName?: string;
  /** z-index del popup (por encima de los mapas de Leaflet). */
  zIndexClassName?: string;
  ariaLabel?: string;
};

/**
 * Popup estándar del panel: fondo con desenfoque, entrada con desplazamiento y nitidez progresiva,
 * salida con desvanecimiento y desenfoque, cierre con Escape y bloqueo del scroll de la página.
 */
export function Modal({
  children,
  onClose,
  maxWidthClassName = 'max-w-3xl',
  showCloseButton = true,
  dismissible = true,
  panelClassName = '',
  zIndexClassName = 'z-[2000]',
  ariaLabel,
}: ModalProps) {
  const [cerrando, setCerrando] = useState(false);
  const cerrandoRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const idRef = useRef(0);
  const panelRef = useRef<HTMLDivElement>(null);

  const cerrar = useCallback(() => {
    if (cerrandoRef.current) return;
    cerrandoRef.current = true;
    setCerrando(true);
    window.setTimeout(() => onCloseRef.current(), DURACION_SALIDA_MS);
  }, []);

  useEffect(() => {
    const id = siguienteModalId++;
    idRef.current = id;
    pilaModales.push(id);
    bloquearScroll();
    const foco = document.activeElement as HTMLElement | null;
    panelRef.current?.focus({ preventScroll: true });
    return () => {
      const indice = pilaModales.indexOf(id);
      if (indice >= 0) pilaModales.splice(indice, 1);
      liberarScroll();
      foco?.focus?.({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    if (!dismissible) return undefined;
    const alPresionar = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && pilaModales[pilaModales.length - 1] === idRef.current) {
        event.stopPropagation();
        cerrar();
      }
    };
    window.addEventListener('keydown', alPresionar);
    return () => window.removeEventListener('keydown', alPresionar);
  }, [cerrar, dismissible]);

  return createPortal(
    <ModalCloseContext.Provider value={cerrar}>
      <div
        className={`fixed inset-0 ${zIndexClassName} flex items-center justify-center px-3 py-4 sm:px-4 sm:py-6`}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
      >
        <div
          className={`absolute inset-0 bg-stone-950/55 backdrop-blur-[3px] ${cerrando ? 'anim-fade-out' : 'anim-fade-in'}`}
          onClick={dismissible ? cerrar : undefined}
          aria-hidden="true"
        />
        <div
          ref={panelRef}
          tabIndex={-1}
          className={`relative max-h-full w-full ${maxWidthClassName} overflow-y-auto rounded-[var(--radius-card)] border border-white/70 bg-white shadow-[0_40px_120px_rgba(28,25,23,0.35)] outline-none ${
            cerrando ? 'anim-modal-out' : 'anim-modal-in'
          } ${panelClassName}`}
        >
          {showCloseButton && (
            <button
              type="button"
              onClick={cerrar}
              aria-label="Cerrar"
              title="Cerrar (Esc)"
              className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-stone-200 bg-white/90 text-stone-500 transition hover:rotate-90 hover:border-[var(--freisa-red)] hover:text-[var(--freisa-red)]"
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
          )}
          {children}
        </div>
      </div>
    </ModalCloseContext.Provider>,
    document.body,
  );
}

// ── Diálogos y avisos (reemplazan alert / confirm / prompt del navegador) ────

type Tono = 'info' | 'exito' | 'error' | 'advertencia' | 'peligro';

type DialogoConfirmacion = {
  tipo: 'confirmar' | 'alerta' | 'texto';
  titulo: string;
  mensaje?: string;
  tono: Tono;
  textoAceptar: string;
  textoCancelar: string;
  campo?: { etiqueta: string; tipo: 'text' | 'password'; placeholder?: string; minimo?: number };
  resolver: (valor: boolean | string | null) => void;
};

type Aviso = { id: number; mensaje: string; tono: Tono; saliendo: boolean };

type Oyente = () => void;

const estado: { dialogos: DialogoConfirmacion[]; avisos: Aviso[] } = { dialogos: [], avisos: [] };
const oyentes = new Set<Oyente>();
let siguienteAvisoId = 1;

function emitir() {
  oyentes.forEach(oyente => oyente());
}

function abrirDialogo(dialogo: Omit<DialogoConfirmacion, 'resolver'>): Promise<boolean | string | null> {
  return new Promise(resolve => {
    estado.dialogos = [...estado.dialogos, { ...dialogo, resolver: resolve }];
    emitir();
  });
}

function cerrarDialogo(dialogo: DialogoConfirmacion, valor: boolean | string | null) {
  estado.dialogos = estado.dialogos.filter(item => item !== dialogo);
  emitir();
  dialogo.resolver(valor);
}

/** Pide confirmación con un diálogo del sistema de diseño. Devuelve true si el usuario acepta. */
export function confirmar(opciones: {
  titulo: string;
  mensaje?: string;
  tono?: Tono;
  textoAceptar?: string;
  textoCancelar?: string;
}): Promise<boolean> {
  return abrirDialogo({
    tipo: 'confirmar',
    titulo: opciones.titulo,
    mensaje: opciones.mensaje,
    tono: opciones.tono ?? 'peligro',
    textoAceptar: opciones.textoAceptar ?? 'Confirmar',
    textoCancelar: opciones.textoCancelar ?? 'Cancelar',
  }).then(Boolean);
}

/** Muestra un mensaje importante que el usuario debe leer (reemplaza alert). */
export function alertar(opciones: { titulo: string; mensaje?: string; tono?: Tono; textoAceptar?: string }): Promise<void> {
  return abrirDialogo({
    tipo: 'alerta',
    titulo: opciones.titulo,
    mensaje: opciones.mensaje,
    tono: opciones.tono ?? 'advertencia',
    textoAceptar: opciones.textoAceptar ?? 'Entendido',
    textoCancelar: '',
  }).then(() => undefined);
}

/** Pide un texto (por ejemplo una contraseña nueva). Devuelve null si se cancela. */
export function pedirTexto(opciones: {
  titulo: string;
  mensaje?: string;
  etiqueta: string;
  tipo?: 'text' | 'password';
  placeholder?: string;
  minimo?: number;
  textoAceptar?: string;
}): Promise<string | null> {
  return abrirDialogo({
    tipo: 'texto',
    titulo: opciones.titulo,
    mensaje: opciones.mensaje,
    tono: 'info',
    textoAceptar: opciones.textoAceptar ?? 'Guardar',
    textoCancelar: 'Cancelar',
    campo: { etiqueta: opciones.etiqueta, tipo: opciones.tipo ?? 'text', placeholder: opciones.placeholder, minimo: opciones.minimo },
  }).then(valor => (typeof valor === 'string' ? valor : null));
}

/** Aviso breve en la esquina (se oculta solo). */
export function avisar(mensaje: string, tono: Tono = 'exito') {
  const id = siguienteAvisoId++;
  estado.avisos = [...estado.avisos, { id, mensaje, tono, saliendo: false }];
  emitir();
  window.setTimeout(() => descartarAviso(id), tono === 'error' ? 6500 : 4000);
}

function descartarAviso(id: number) {
  if (!estado.avisos.some(aviso => aviso.id === id && !aviso.saliendo)) return;
  estado.avisos = estado.avisos.map(aviso => (aviso.id === id ? { ...aviso, saliendo: true } : aviso));
  emitir();
  window.setTimeout(() => {
    estado.avisos = estado.avisos.filter(aviso => aviso.id !== id);
    emitir();
  }, DURACION_SALIDA_MS + 40);
}

/** Texto del error de una petición (o el mensaje por defecto). */
export function mensajeDeError(error: unknown, porDefecto: string): string {
  const mensaje = (error as { message?: string } | null)?.message;
  return typeof mensaje === 'string' && mensaje.trim() ? mensaje : porDefecto;
}

const estiloTono: Record<Tono, { icono: string; circulo: string; boton: string; aviso: string }> = {
  info: {
    icono: 'fa-solid fa-circle-info',
    circulo: 'bg-blue-50 text-blue-600',
    boton: 'bg-stone-900 hover:bg-stone-700',
    aviso: 'border-blue-200 text-blue-800',
  },
  exito: {
    icono: 'fa-solid fa-circle-check',
    circulo: 'bg-emerald-50 text-emerald-600',
    boton: 'bg-emerald-600 hover:bg-emerald-700',
    aviso: 'border-emerald-200 text-emerald-800',
  },
  error: {
    icono: 'fa-solid fa-circle-exclamation',
    circulo: 'bg-red-50 text-[var(--freisa-red)]',
    boton: 'bg-[var(--freisa-red)] hover:bg-[var(--freisa-red-dark)]',
    aviso: 'border-red-200 text-red-800',
  },
  advertencia: {
    icono: 'fa-solid fa-triangle-exclamation',
    circulo: 'bg-amber-50 text-amber-600',
    boton: 'bg-stone-900 hover:bg-stone-700',
    aviso: 'border-amber-200 text-amber-800',
  },
  peligro: {
    icono: 'fa-solid fa-trash-can',
    circulo: 'bg-red-50 text-[var(--freisa-red)]',
    boton: 'bg-[var(--freisa-red)] hover:bg-[var(--freisa-red-dark)]',
    aviso: 'border-red-200 text-red-800',
  },
};

function useEstadoFeedback() {
  const [, setVersion] = useState(0);
  useEffect(() => {
    const oyente = () => setVersion(version => version + 1);
    oyentes.add(oyente);
    return () => {
      oyentes.delete(oyente);
    };
  }, []);
  return estado;
}

function DialogoView({ dialogo }: { dialogo: DialogoConfirmacion }) {
  const [texto, setTexto] = useState('');
  const estilo = estiloTono[dialogo.tono];
  const minimo = dialogo.campo?.minimo ?? 1;
  const textoValido = dialogo.tipo !== 'texto' || texto.trim().length >= minimo;
  const valorCancelado = dialogo.tipo === 'texto' ? null : false;
  const resultadoRef = useRef<boolean | string | null>(valorCancelado);

  return (
    <Modal
      onClose={() => cerrarDialogo(dialogo, resultadoRef.current)}
      maxWidthClassName="max-w-md"
      showCloseButton={false}
      zIndexClassName="z-[3000]"
      ariaLabel={dialogo.titulo}
    >
      <DialogoContenido
        dialogo={dialogo}
        estilo={estilo}
        texto={texto}
        setTexto={setTexto}
        textoValido={textoValido}
        minimo={minimo}
        alAceptar={() => {
          resultadoRef.current = dialogo.tipo === 'texto' ? texto : true;
        }}
        alCancelar={() => {
          resultadoRef.current = valorCancelado;
        }}
      />
    </Modal>
  );
}

function DialogoContenido({
  dialogo,
  estilo,
  texto,
  setTexto,
  textoValido,
  minimo,
  alAceptar,
  alCancelar,
}: {
  dialogo: DialogoConfirmacion;
  estilo: (typeof estiloTono)[Tono];
  texto: string;
  setTexto: (valor: string) => void;
  textoValido: boolean;
  minimo: number;
  alAceptar: () => void;
  alCancelar: () => void;
}) {
  const cerrar = useCerrarModal();
  const aceptar = () => {
    if (!textoValido) return;
    alAceptar();
    cerrar();
  };

  return (
    <div className="p-6">
      <div className="flex items-start gap-4">
        <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-lg ${estilo.circulo}`}>
          <i className={estilo.icono} aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1 pt-1">
          <h3 className="text-lg font-semibold text-stone-950">{dialogo.titulo}</h3>
          {dialogo.mensaje && (
            <p className="mt-2 whitespace-pre-line text-sm leading-6 text-stone-600">{dialogo.mensaje}</p>
          )}
        </div>
      </div>

      {dialogo.campo && (
        <div className="mt-5">
          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
            {dialogo.campo.etiqueta}
          </label>
          <input
            type={dialogo.campo.tipo}
            autoFocus
            value={texto}
            placeholder={dialogo.campo.placeholder}
            onChange={event => setTexto(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') aceptar();
            }}
            className="ui-input"
          />
          {minimo > 1 && (
            <p className={`mt-2 text-xs ${textoValido ? 'text-stone-400' : 'text-amber-700'}`}>Mínimo {minimo} caracteres.</p>
          )}
        </div>
      )}

      <div className="mt-6 flex justify-end gap-3">
        {dialogo.tipo !== 'alerta' && (
          <button
            type="button"
            onClick={() => {
              alCancelar();
              cerrar();
            }}
            className="ui-btn ui-btn-secondary"
          >
            {dialogo.textoCancelar}
          </button>
        )}
        <button
          type="button"
          autoFocus={dialogo.tipo !== 'texto'}
          disabled={!textoValido}
          onClick={aceptar}
          className={`rounded-[14px] px-4 py-2.5 text-sm font-semibold text-white transition disabled:opacity-50 ${estilo.boton}`}
        >
          {dialogo.textoAceptar}
        </button>
      </div>
    </div>
  );
}

/** Monta los diálogos y avisos. Debe estar una sola vez en la raíz de la app. */
export function FeedbackHost() {
  const { dialogos, avisos } = useEstadoFeedback();

  return (
    <>
      {dialogos.map((dialogo, indice) => (
        <DialogoView key={indice} dialogo={dialogo} />
      ))}
      {createPortal(
        <div className="pointer-events-none fixed inset-x-3 top-3 z-[3100] flex flex-col items-end gap-2 sm:inset-x-auto sm:right-5 sm:top-5 sm:w-[380px]">
          {avisos.map(aviso => {
            const estilo = estiloTono[aviso.tono];
            return (
              <div
                key={aviso.id}
                role="status"
                className={`pointer-events-auto flex w-full items-start gap-3 rounded-[var(--radius-panel)] border bg-white/95 px-4 py-3 text-sm shadow-[0_18px_40px_rgba(28,25,23,0.18)] backdrop-blur ${estilo.aviso} ${
                  aviso.saliendo ? 'anim-toast-out' : 'anim-toast-in'
                }`}
              >
                <i className={`${estilo.icono} mt-0.5`} aria-hidden="true" />
                <p className="min-w-0 flex-1 whitespace-pre-line font-medium text-stone-800">{aviso.mensaje}</p>
                <button
                  type="button"
                  aria-label="Cerrar aviso"
                  onClick={() => descartarAviso(aviso.id)}
                  className="text-stone-400 transition hover:text-stone-700"
                >
                  <i className="fa-solid fa-xmark" aria-hidden="true" />
                </button>
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
}

/** Filas de esqueleto mientras carga una lista o tabla. */
export function SkeletonRows({ etiqueta = 'Cargando...', filas = 4 }: { etiqueta?: string; filas?: number }) {
  return (
    <div role="status" aria-busy="true" className="ui-card space-y-3 p-5">
      <span className="sr-only">{etiqueta}</span>
      {Array.from({ length: filas }, (_, indice) => (
        <div key={indice} className="flex items-center gap-4" style={{ opacity: 1 - indice * 0.15 }}>
          <div className="ui-skeleton h-9 w-9 shrink-0 rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="ui-skeleton h-3" style={{ width: `${70 - (indice % 3) * 15}%` }} />
            <div className="ui-skeleton h-3 w-1/3" />
          </div>
          <div className="ui-skeleton hidden h-8 w-24 sm:block" />
        </div>
      ))}
    </div>
  );
}

/** Barra de progreso indeterminada (recarga de datos que ya están en pantalla). */
export function BarraProgreso({ etiqueta = 'Actualizando...' }: { etiqueta?: string }) {
  return (
    <div role="status" aria-label={etiqueta} className="ui-progreso">
      <span className="sr-only">{etiqueta}</span>
    </div>
  );
}

/**
 * Posición de un menú desplegable de la cabecera: en pantallas angostas (< 640 px) se fija a lo ancho
 * de la pantalla justo debajo del botón para que no se salga por los lados; en el resto usa su CSS normal.
 */
export function usePosicionDesplegable(abierto: boolean, anclaRef: { current: HTMLElement | null }) {
  const [estilo, setEstilo] = useState<CSSProperties | undefined>(undefined);

  useEffect(() => {
    if (!abierto) return undefined;
    const calcular = () => {
      const ancla = anclaRef.current;
      if (!ancla || window.innerWidth >= 640) {
        setEstilo(undefined);
        return;
      }
      const rect = ancla.getBoundingClientRect();
      setEstilo({ position: 'fixed', top: rect.bottom + 10, left: 12, right: 12, width: 'auto' });
    };
    calcular();
    window.addEventListener('resize', calcular);
    return () => window.removeEventListener('resize', calcular);
  }, [abierto, anclaRef]);

  return estilo;
}
